import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import CategoryTabs from '@/components/base/CategoryTabs';
import SEOSchema from '@/components/base/SEOSchema';
import { articles, categories } from '@/mocks/articles';

const ITEMS_PER_PAGE = 9;

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [currentPage, setCurrentPage] = useState(1);

  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryName = currentCategory?.name || slug || '';

  const filteredArticles = slug
    ? articles.filter((a) => a.categorySlug === slug)
    : articles;

  const sortedArticles = [...filteredArticles].sort(
    (a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
  );

  const totalPages = Math.ceil(sortedArticles.length / ITEMS_PER_PAGE);
  const paginatedArticles = sortedArticles.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  if (!currentCategory) {
    return (
      <div className="min-h-screen bg-background-50 text-foreground-950">
        <Navbar />
        <main className="pt-20 pb-10">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8 text-center py-20">
            <h1 className="text-2xl font-bold">分类不存在</h1>
            <Link to="/" className="mt-4 inline-block text-accent-500 hover:text-accent-600">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <ScrollToTop />
      <SEOSchema type="Organization" />
      <SEOSchema type="WebSite" />
      <SEOSchema
        type="WebPage"
        data={{
          name: `${categoryName} - 黑料爆料`,
          url: `https://heiliaobaoliao.com/category/${slug}`,
        }}
      />

      <div className="min-h-screen bg-background-50 text-foreground-950">
        <Navbar />

        <main className="pt-20 pb-10 md:pb-14">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            {/* Breadcrumb */}
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: categoryName },
                ]}
              />
            </div>

            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-10 h-10 rounded-lg bg-accent-500/10 flex items-center justify-center">
                  <i className={`${currentCategory.icon} text-accent-500 text-lg`}></i>
                </span>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">
                    {categoryName}
                  </h1>
                  <p className="text-sm text-foreground-500 mt-0.5">
                    共 {filteredArticles.length} 篇爆料
                  </p>
                </div>
              </div>
            </div>

            {/* Category Tabs */}
            <div className="mb-8">
              <CategoryTabs categories={categories} activeSlug={slug || 'all'} />
            </div>

            {/* Articles Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {paginatedArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>

            {paginatedArticles.length === 0 && (
              <div className="text-center py-16">
                <i className="ri-inbox-line text-4xl text-foreground-400"></i>
                <p className="mt-3 text-foreground-500">暂无该分类的爆料内容</p>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <nav className="mt-10 flex items-center justify-center gap-2" aria-label="分页导航">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 border border-background-200/50 text-foreground-600 hover:text-accent-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-10 h-10 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                      currentPage === page
                        ? 'bg-accent-500 text-white'
                        : 'bg-background-100 border border-background-200/50 text-foreground-600 hover:text-accent-500'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 border border-background-200/50 text-foreground-600 hover:text-accent-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </nav>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}